import React, { useEffect, useRef, useState } from "react";
import Overlay from "react-bootstrap/Overlay";
import ReactDatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import dateFormat from "date-fns/format";
import { twMerge } from "tailwind-merge";

type DatePickerProps = {
  value?: Date;
  onSelect?: (_date?: Date) => void;
  format?: string;
  maxDate?: Date;
  minDate?: Date;
  showSetToNow?: boolean;
  removeButton?: React.ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  placeholder?: string;
};

const TEAL_STYLES = `
  .sk-datepicker .react-datepicker {
    border-color: #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    font-family: inherit;
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
  }
  .sk-datepicker .react-datepicker__header {
    background: #f0fafa;
    border-bottom-color: #d1f0ef;
  }
  .sk-datepicker .react-datepicker__current-month,
  .sk-datepicker .react-datepicker__day-name {
    color: #24a9a7;
  }
  .sk-datepicker .react-datepicker__navigation-icon::before {
    border-color: #24a9a7;
  }
  .sk-datepicker .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
    border-color: #1a8a88;
  }
  .sk-datepicker .react-datepicker__day:hover {
    background: rgba(36, 169, 167, 0.12);
    color: #24a9a7;
    border-radius: 6px;
  }
  .sk-datepicker .react-datepicker__day--selected,
  .sk-datepicker .react-datepicker__day--keyboard-selected {
    background: #24a9a7;
    color: #fff;
    border-radius: 6px;
  }
  .sk-datepicker .react-datepicker__day--selected:hover,
  .sk-datepicker .react-datepicker__day--keyboard-selected:hover {
    background: #1a8a88;
  }
  .sk-datepicker .react-datepicker__day--in-range {
    background: rgba(36, 169, 167, 0.15);
    color: #24a9a7;
  }
  .sk-datepicker .react-datepicker__day--in-selecting-range {
    background: rgba(36, 169, 167, 0.3);
  }
  .sk-datepicker .react-datepicker__day--today {
    font-weight: bold;
    color: #24a9a7;
  }
  .sk-datepicker .react-datepicker__day--today.react-datepicker__day--selected,
  .sk-datepicker .react-datepicker__day--today.react-datepicker__day--keyboard-selected {
    color: #fff !important;
  }
  .sk-datepicker .react-datepicker__day--outside-month {
    color: #bbb;
  }
`;

const DatePicker = ({
  value,
  onSelect,
  format = "d MMM yyyy",
  showSetToNow,
  removeButton,
  containerClassName,
  inputClassName,
  placeholder,
  ...datePickerProps
}: DatePickerProps) => {
  const target = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState(value);
  const [showing, setShowing] = useState(false);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <div className={twMerge(containerClassName)}>
      <style>{TEAL_STYLES}</style>
      <input
        ref={target}
        onClick={() => setShowing(true)}
        className={twMerge(
          "px-[12px] py-[6px] shadow-inner border border-solid border-[#ddd]",
          "w-full cursor-pointer focus:outline-none focus:border-[#24a9a7] transition-colors",
          inputClassName
        )}
        placeholder={placeholder || "Select date"}
        readOnly
        value={selected ? dateFormat(selected, format) : ""}
      />
      {selected ? (
        removeButton ? (
          <div
            className=""
            onClick={() => {
              setSelected(undefined);
              onSelect && onSelect(undefined);
            }}
          >
            {removeButton}
          </div>
        ) : (
          <div>
            <button
              className="p-0 outline-none border-0 bg-transparent text-bearish-red hover:text-[#85273a] mt-[5px]"
              onClick={() => {
                setSelected(undefined);
                onSelect && onSelect(undefined);
              }}
            >
              Remove
            </button>
          </div>
        )
      ) : showSetToNow ? (
        <button
          className="p-0 outline-none border-0 bg-transparent text-smart-teal hover:text-dark-teal mt-[5px]"
          onClick={() => {
            const now = new Date();
            setSelected(now);
            onSelect && onSelect(now);
          }}
        >
          Set To Now
        </button>
      ) : null}
      <Overlay
        target={target.current}
        show={showing}
        placement="bottom"
        rootClose
        onHide={() => setShowing(false)}
      >
        {(props) => (
          <div
            {...props}
            onMouseEnter={() => setShowing(true)}
            onMouseLeave={() => setShowing(false)}
            className="absolute z-[2000] sk-datepicker"
          >
            <ReactDatePicker
              {...datePickerProps}
              inline
              selected={selected}
              onChange={(date: Date) => {
                setSelected(date);
                onSelect && onSelect(date);
              }}
              onSelect={() => {
                setShowing(false);
              }}
            />
          </div>
        )}
      </Overlay>
    </div>
  );
};

export default DatePicker;
